import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Severity,
  type SeverityType,
} from '@kirin/api-client/contracts/shared/severity';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import {
  removeItemByIndex,
  SEVERITY_OPTIONS,
  useSortedItemsWithIndices,
} from '../helpers';
import { SeverityBadge } from './SeverityBadge';

interface PatternArrayInputProps {
  value: Array<{
    pattern: string;
    reason?: string; // Legacy field name
    description?: string; // New field name
    severity: SeverityType;
  }>;
  onChange: (
    value: Array<{
      pattern: string;
      reason?: string;
      description?: string;
      severity: SeverityType;
    }>
  ) => void;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    properties?: Record<string, { type?: string; required?: boolean }>;
  };
  disabled?: boolean;
  policyKey?: string;
  settingKey?: string;
}

export function PatternArrayInput({
  value,
  onChange,
  disabled = false,
  policyKey,
  settingKey,
}: PatternArrayInputProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [newPattern, setNewPattern] = useState<{
    pattern: string;
    reason: string;
    severity: SeverityType;
  }>({
    pattern: '',
    reason: '',
    severity: Severity.Medium,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const PREVIEW_COUNT = 3;

  // Determine help text based on policy type and setting
  const getHelpText = () => {
    if (policyKey === 'mcp-security' && settingKey === 'suspiciousPatterns') {
      return 'Define regex patterns to detect suspicious tool descriptions. Use standard regex syntax.';
    }
    if (
      policyKey === 'extension-security' &&
      settingKey === 'untrustedPublishers'
    ) {
      return 'Define patterns for untrusted publisher names.';
    }

    // Context-specific help for cursor-database patterns
    if (policyKey === 'cursor-database') {
      if (settingKey === 'maliciousPatterns') {
        return 'Patterns for detecting security risks in AI-generated source code (eval, exec, SQL injection, etc.)';
      }
      if (settingKey === 'commandPatterns') {
        return 'Patterns for detecting dangerous CLI commands (rm -rf, curl|sh, reverse shells, etc.)';
      }
    }

    // Context-specific help for secret-detection patterns
    if (policyKey === 'secret-detection' && settingKey === 'customPatterns') {
      return 'Custom regex patterns for detecting secrets. Patterns are collapsed by default but can be expanded to configure.';
    }

    // Default
    return 'Define regex patterns to detect suspicious patterns. Use standard regex syntax.';
  };

  const getPatternIcon = () => {
    if (settingKey === 'maliciousPatterns') return null;
    if (settingKey === 'commandPatterns') return null;
    return null;
  };

  // Sort items with their original indices
  const sortedWithIndices = useSortedItemsWithIndices(value);

  // Show only first 3 by default
  const visibleItems = isExpanded
    ? sortedWithIndices
    : sortedWithIndices.slice(0, PREVIEW_COUNT);

  const hasMore = sortedWithIndices.length > PREVIEW_COUNT;
  const icon = getPatternIcon();

  const validatePattern = (pattern: {
    pattern: string;
    reason: string;
    severity: string;
  }) => {
    const newErrors: Record<string, string> = {};

    if (!pattern.pattern.trim()) {
      newErrors.pattern = 'Pattern is required';
    } else {
      // Validate maxLength for pattern (500 characters)
      if (pattern.pattern.length > 500) {
        newErrors.pattern = 'Pattern must be 500 characters or less';
        setErrors(newErrors);
        return false;
      }
      // Validate that the pattern is a syntactically valid regular expression
      try {
        new RegExp(pattern.pattern);
      } catch {
        newErrors.pattern = 'Invalid regular expression';
      }
    }

    if (!pattern.reason.trim()) {
      newErrors.reason = 'Description is required';
    } else {
      // Validate maxLength for reason/description (200 characters)
      if (pattern.reason.length > 200) {
        newErrors.reason = 'Description must be 200 characters or less';
        setErrors(newErrors);
        return false;
      }
    }

    if (!pattern.severity) {
      newErrors.severity = 'Severity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addPattern = () => {
    if (validatePattern(newPattern)) {
      // Map reason to description for secret-detection policy to match schema
      const patternToAdd =
        policyKey === 'secret-detection'
          ? {
              pattern: newPattern.pattern,
              description: newPattern.reason,
              severity: newPattern.severity,
            }
          : newPattern;
      onChange([...value, patternToAdd]);
      setNewPattern({
        pattern: '',
        reason: '',
        severity: Severity.Medium,
      });
      setIsAdding(false);
      setErrors({});
    }
  };

  const removePattern = (originalIndex: number) => {
    onChange(removeItemByIndex(value, originalIndex));
  };

  return (
    <div className="space-y-3">
      {/* Help text */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        {icon && <span className="text-base">{icon}</span>}
        <span>{getHelpText()}</span>
      </div>

      {value.length > 0 && (
        <div className="space-y-2">
          {/* Scrollable container when expanded */}
          <div
            className={`space-y-2 ${
              isExpanded && hasMore
                ? 'max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent'
                : ''
            }`}
          >
            {visibleItems.map(({ item, originalIndex }) => {
              return (
                <div
                  key={`${item.pattern}-${item.description || item.reason}-${item.severity}-${originalIndex}`}
                  className="group border rounded-md p-2.5 bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xs font-mono bg-background px-2 py-0.5 rounded border truncate">
                          {item.pattern}
                        </code>
                        <SeverityBadge riskLevel={item.severity} showIcon />
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {item.description || item.reason || 'No description'}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removePattern(originalIndex)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={disabled}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand/Collapse button */}
          {hasMore && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full text-xs h-8 text-muted-foreground hover:text-foreground"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5 mr-1" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5 mr-1" />
                  Show {sortedWithIndices.length - PREVIEW_COUNT} more
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Add pattern form */}
      {isAdding ? (
        <div className="border rounded-md p-3 space-y-3 bg-muted/20">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Pattern</Label>
            <Input
              value={newPattern.pattern}
              onChange={e =>
                setNewPattern(prev => ({ ...prev, pattern: e.target.value }))
              }
              placeholder="e.g., eval\\s*\\("
              className={`h-8 text-xs ${errors.pattern ? 'border-red-500' : ''}`}
              disabled={disabled}
              maxLength={500} // Maximum 500 characters for regex patterns
            />
            {errors.pattern && (
              <div className="text-xs text-red-500">{errors.pattern}</div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Input
              value={newPattern.reason}
              onChange={e =>
                setNewPattern(prev => ({ ...prev, reason: e.target.value }))
              }
              placeholder="Brief description..."
              className={`h-8 text-xs ${errors.reason ? 'border-red-500' : ''}`}
              disabled={disabled}
              maxLength={200} // Maximum 200 characters for description
            />
            {errors.reason && (
              <div className="text-xs text-red-500">{errors.reason}</div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Severity</Label>
            <select
              value={newPattern.severity}
              onChange={e =>
                setNewPattern(prev => ({
                  ...prev,
                  severity: e.target.value as SeverityType,
                }))
              }
              className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              disabled={disabled}
            >
              {SEVERITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              onClick={addPattern}
              disabled={disabled}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setNewPattern({
                  pattern: '',
                  reason: '',
                  severity: Severity.Medium,
                });
                setErrors({});
              }}
              disabled={disabled}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setIsAdding(true)}
          disabled={disabled}
          className="h-8 text-xs w-full sm:w-auto"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Custom Pattern
        </Button>
      )}
    </div>
  );
}
